import React from 'react'
import { isEmpty } from 'lodash'

import { Tag, PriorityLabel } from 'components'
import {
  setValue,
  setFormatAssignDate,
  getPhoneFormatter,
} from 'helpers/common'
import { TicketLabel } from 'features/Ticket'
import { getLabel } from 'helpers/language'

import type { TicketDetail } from 'features/Ticket'
import type { TicketLabelProps } from 'features/Ticket/TicketLabel'

const renderAssignDescription = (
  users: {
    fullName: string
    phoneNumber?: string
  }[],
): JSX.Element[] | string => {
  const base = 'f-ticket-info'

  const getAssignTechnicians = users.map(({ fullName, phoneNumber }, index) => {
    const technicianFullName = (
      <span className={`${base}__technician-full-name`}>{`${fullName} `}</span>
    )
    const technicianPhoneNumber =
      phoneNumber && `(${getPhoneFormatter(phoneNumber)})`
    const pipeString = index !== users.length - 1 && (
      <span className={`${base}__pipe`}>|</span>
    )

    return (
      <span
        className={`${base}__assign-technicians`}
        key={`technician-${index}`}
      >
        {technicianFullName}
        {technicianPhoneNumber}
        {pipeString}
      </span>
    )
  })

  return isEmpty(users) ? '-' : getAssignTechnicians
}

const renderTicketInfo = (
  detail: TicketDetail,
  isLoading: boolean,
): JSX.Element[] => {
  const base = 'f-ticket-info'
  const {
    requestDate = '',
    statusProgress = '',
    statusPriority = '',
    users = [],
    issueDetail = '',
    products = [],
  } = detail

  const _requestDate = requestDate
    ? setFormatAssignDate(new Date(requestDate))
    : '-'

  const list: TicketLabelProps[] = [
    {
      key: 'status',
      title: 'ticket:status.title',
      valueElement: <Tag status={setValue(statusProgress)} />,
      skeletonWidthTitle: '65px',
      skeletonWidthValue: '92px',
      isRow: true,
    },
    {
      key: 'priority',
      title: 'ticket:priorityLevel.label',
      valueElement: (
        <PriorityLabel
          classNameText={`${base}__value`}
          status={setValue(statusPriority)}
        />
      ),
      skeletonWidthTitle: '65px',
      skeletonWidthValue: '67px',
      isRow: true,
    },
    {
      key: 'requestDate',
      title: 'ticket:requestDate.title',
      value: _requestDate,
      skeletonWidthTitle: '92px',
      skeletonWidthValue: '67px',
      isRow: true,
    },
    {
      key: 'assignedToAndAssignedPhoneNumber',
      title: 'ticket:assignedToAndAssignedPhoneNumber.label',
      value: renderAssignDescription(users),
      skeletonWidthTitle: '120px',
      skeletonWidthValue: '100%',
      isRow: true,
    },
    {
      key: 'numberProducts',
      title: 'product:numberProducts.label',
      valueElement: (
        <span className='f-ticket-label__value'>
          {products.length} {getLabel('common:list.label')}
        </span>
      ),
      skeletonWidthTitle: '65px',
      skeletonWidthValue: '67px',
      isRow: true,
    },
    {
      key: 'description',
      title: 'common:description.label',
      value: setValue(issueDetail),
      skeletonWidthTitle: '65px',
      isRow: false,
    },
  ]

  return list.map((item, index) => (
    <TicketLabel
      key={`ticket-info-${index}`}
      isLoading={isLoading}
      title={item.title}
      value={item.value}
      valueElement={item.valueElement}
      skeletonWidthTitle={item.skeletonWidthTitle}
      skeletonWidthValue={item.skeletonWidthValue}
      isShowSkeletonTitle
      isRow={item.isRow}
    />
  ))
}

export default renderTicketInfo
